import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PasswordInput from './PasswordInput';
import { formatPhone, validatePhone, COUNTRY_CONFIGS } from '../utils/formatters';

const API = '/api';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const ic = {
    building: ['M3 21h18', 'M9 8h1', 'M9 12h1', 'M9 16h1', 'M14 8h1', 'M14 12h1', 'M14 16h1', 'M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16'],
    phone: ['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z', 'M12 10m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0'],
    plus: 'M12 5v14 M5 12h14',
    x: 'M18 6L6 18 M6 6l12 12',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6'
};

export default function MaintenanceWorkshopVendor({ user, operatorDetails }) {
    const [workshops, setWorkshops] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingWorkshop, setEditingWorkshop] = useState(null);
    const [formData, setFormData] = useState({
        name: '', contactPerson: '', email: '', password: '', phone: '', countryCode: '', address: '', gstNumber: '', paymentStatus: 'Pending'
    });

    useEffect(() => {
        fetchWorkshops();
        fetchCountries();
    }, [user]);

    const fetchCountries = async () => {
        try {
            const res = await axios.get(`${API}/countries`);
            setCountries(res.data);
            if (!formData.countryCode) {
                const config = COUNTRY_CONFIGS[operatorDetails?.country] || Object.values(COUNTRY_CONFIGS)[0];
                setFormData(prev => ({ ...prev, countryCode: config?.phoneCode || '+91' }));
            }
        } catch (err) {
            console.error('Fetch countries error:', err);
        }
    };

    const fetchWorkshops = async () => {
        setLoading(true);
        try {
            const opId = user?.operatorId || user?._id || 'all';
            const res = await axios.get(`${API}/workshops/${opId}`);
            setWorkshops(res.data);
        } catch (err) {
            console.error('Fetch workshops error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData, operatorId: user?.operatorId || user?._id };
            if (editingWorkshop) {
                await axios.put(`${API}/workshops/${editingWorkshop._id}`, payload);
            } else {
                await axios.post(`${API}/workshops`, payload);
            }
            setShowModal(false);
            setEditingWorkshop(null);
            const config = COUNTRY_CONFIGS[operatorDetails?.country] || Object.values(COUNTRY_CONFIGS)[0];
            setFormData({ name: '', contactPerson: '', email: '', password: '', phone: '', countryCode: config?.phoneCode || '+91', address: '', gstNumber: '', paymentStatus: 'Pending' });
            fetchWorkshops();
        } catch (err) {
            console.error('Save workshop error:', err);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
        try {
            await axios.delete(`${API}/workshops/${id}`);
            fetchWorkshops();
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const handleEdit = (w) => {
        setEditingWorkshop(w);
        setFormData({
            name: w.name,
            contactPerson: w.contactPerson || '',
            email: w.email || '',
            password: w.password || '',
            phone: w.phone || '',
            countryCode: w.countryCode || countries[0]?.code || '',
            address: w.address || '',
            gstNumber: w.gstNumber || '',
            paymentStatus: w.paymentStatus || 'Pending'
        });
        setShowModal(true);
    };

    const StatusBadge = ({ status }) => {
        let style = { background: '#f1f5f9', color: '#64748b' };
        if (status === 'Paid') style = { background: '#ecfdf5', color: '#059669' };
        if (status === 'Overdue') style = { background: '#fef2f2', color: '#dc2626' };
        if (status === 'Pending') style = { background: '#f8fafc', color: '#444', border: '1px solid #e2e8f0' };

        return (
            <span style={{
                ...style,
                padding: '4px 14px',
                borderRadius: '99px',
                fontSize: '11px',
                fontWeight: '700',
                display: 'inline-block'
            }}>
                {status}
            </span>
        );
    };

    return (
        <div className="vw-page">
            <style>{`
                .vw-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .vw-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .vw-title { font-size: 26px; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
                .vw-subtitle { font-size: 14px; color: #64748b; margin: 0; }
                
                .vw-add-btn {
                    background: #00B2A9; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    font-size: 14px; box-shadow: 0 4px 12px rgba(99,102,241,0.2);
                }

                .vw-card { 
                    background: white; border-radius: 12px; border: 1px solid #e2e8f0; 
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03); overflow: hidden;
                    margin-top: 10px;
                }
                .vw-table { width: 100%; border-collapse: collapse; }
                .vw-table th { 
                    text-align: left; padding: 16px 20px; font-size: 12px; font-weight: 600; 
                    color: #94a3b8; text-transform: none; border-bottom: 1px solid #f1f5f9;
                }
                .vw-table td { padding: 16px 20px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .vw-table tr:hover { background: #fafbfc; }

                .vw-name-cell { display: flex; align-items: center; gap: 12px; font-weight: 700; color: #1e293b; }
                .vw-icon-circle { 
                    width: 28px; height: 28px; border-radius: 6px; border: 1px solid #eef2ff;
                    display: flex; align-items: center; justify-content: center; color: #00B2A9;
                }
                
                .vw-address-info { color: #64748b; font-size: 13px; display: flex; align-items: flex-start; gap: 6px; max-width: 300px; }

                .vw-actions { display: flex; gap: 8px; }
                .vw-act-btn { 
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .vw-act-btn:hover { background: #f1f5f9; color: #00B2A9; }
                .vw-act-btn.delete:hover { background: #fef2f2; color: #ef4444; }

                /* Modal Styles */
                .vw-modal-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .vw-modal { background: white; width: 650px; border-radius: 20px; padding: 30px; position: relative; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); }
                .vw-modal-close { position: absolute; top: 20px; right: 20px; cursor: pointer; color: #94a3b8; }
                .vw-modal-title { font-size: 20px; font-weight: 800; color: #1e293b; margin-bottom: 24px; }
                
                .vw-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
                .vw-form-group { display: flex; flex-direction: column; gap: 6px; }
                .vw-form-group.full { grid-column: span 2; }
                .vw-label { font-size: 12px; font-weight: 700; color: #475569; }
                .vw-label span { color: #ef4444; }
                .vw-input, .vw-select, .vw-phone-input {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px;
                    outline: none; transition: all 0.2s; background: #fff;
                }
                .vw-input:focus, .vw-select:focus, .vw-phone-input:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
                .vw-input::placeholder { color: #cbd5e1; }

                .vw-phone-wrap { display: flex; gap: 8px; }
                .vw-cc-select { width: 90px; flex-shrink: 0; }
                .vw-phone-input { flex: 1; }

                .vw-save-btn {
                    width: 100%; background: #00B2A9; color: white; border: none; padding: 12px;
                    border-radius: 10px; font-weight: 700; cursor: pointer; margin-top: 10px;
                }
            `}</style>

            <div className="vw-header">
                <div>
                    <h1 className="vw-title">Vendors & Workshops</h1>
                    <p className="vw-subtitle">Manage external service providers and payments.</p>
                </div>
                <button className="vw-add-btn" onClick={() => setShowModal(true)}>
                    <Icon d={ic.plus} size={18} strokeWidth={2.5} /> Add Vendor
                </button>
            </div>

            <div className="vw-card">
                <table className="vw-table">
                    <thead>
                        <tr>
                            <th style={{ width: '30%' }}>Vendor Name</th>
                            <th style={{ width: '20%' }}>Contact</th>
                            <th style={{ width: '15%' }}>Phone</th>
                            <th style={{ width: '25%' }}>Address</th>
                            <th style={{ width: '10%' }}>Payment Status</th>
                            <th style={{ width: '10%' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading vendors...</td></tr>
                        ) : workshops.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No vendors found. Click "Add Vendor" to create one.</td></tr>
                        ) : (
                            workshops.map(w => (
                                <tr key={w._id}>
                                    <td>
                                        <div className="vw-name-cell">
                                            <div className="vw-icon-circle">
                                                <Icon d={ic.building} size={15} />
                                            </div>
                                            {w.name}
                                        </div>
                                    </td>
                                    <td><div className="vw-contact-info">{w.contactPerson || '-'}</div></td>
                                    <td>
                                        <div className="vw-contact-info">
                                            <Icon d={ic.phone} size={13} color="#94a3b8" />
                                            {formatPhone(w.phone, operatorDetails?.country)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="vw-address-info">
                                            <Icon d={ic.mapPin} size={13} style={{ marginTop: 2 }} />
                                            {w.address || '-'}
                                        </div>
                                    </td>
                                    <td>
                                        <StatusBadge status={w.paymentStatus} />
                                    </td>
                                    <td>
                                        <div className="vw-actions">
                                            <button className="vw-act-btn" title="Edit" onClick={() => handleEdit(w)}>
                                                <Icon d={ic.edit} size={16} />
                                            </button>
                                            <button className="vw-act-btn delete" title="Delete" onClick={() => handleDelete(w._id, w.name)}>
                                                <Icon d={ic.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="vw-modal-overlay">
                    <div className="vw-modal">
                        <div className="vw-modal-close" onClick={() => { setShowModal(false); setEditingWorkshop(null); setFormData({ name: '', contactPerson: '', email: '', password: '', phone: '', countryCode: countries[0]?.code || '', address: '', gstNumber: '', paymentStatus: 'Pending' }); }}>
                            <Icon d={ic.x} size={20} />
                        </div>
                        <h2 className="vw-modal-title">{editingWorkshop ? 'Edit Vendor / Workshop' : 'Add Vendor / Workshop'}</h2>
                        <form onSubmit={handleSave}>
                            <div className="vw-form-grid">
                                <div className="vw-form-group">
                                    <label className="vw-label">Vendor Name <span>*</span></label>
                                    <input
                                        type="text" className="vw-input" required
                                        value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="vw-form-group">
                                    <label className="vw-label">Contact Person <span>*</span></label>
                                    <input
                                        type="text" className="vw-input" required
                                        value={formData.contactPerson} onChange={e => setFormData({ ...formData, contactPerson: e.target.value })}
                                    />
                                </div>
                                <div className="vw-form-group">
                                    <label className="vw-label">Email <span>*</span></label>
                                    <input
                                        type="email" className="vw-input"
                                        placeholder="Enter email"
                                        value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>
                                <div className="vw-form-group">
                                    <label className="vw-label">Password</label>
                                    <PasswordInput
                                        className="vw-input"
                                        placeholder="Enter password"
                                        value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    />
                                </div>

                                <div className="vw-form-group">
                                    <label className="vw-label">Phone <span>*</span></label>
                                    <div className="vw-phone-wrap">
                                        <select
                                            className="vw-select vw-cc-select"
                                            value={formData.countryCode}
                                            onChange={e => setFormData({ ...formData, countryCode: e.target.value })}
                                        >
                                            {Object.values(COUNTRY_CONFIGS).map(c => (
                                                <option key={c.phoneCode} value={c.phoneCode}>{c.phoneCode}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text" className="vw-phone-input" required
                                            placeholder="Number"
                                            value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="vw-form-group full">
                                    <label className="vw-label">Address <span>*</span></label>
                                    <input
                                        type="text" className="vw-input" required
                                        value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                                    />
                                </div>
                                <div className="vw-form-group">
                                    <label className="vw-label">GST Number</label>
                                    <input
                                        type="text" className="vw-input" placeholder="Optional"
                                        value={formData.gstNumber} onChange={e => setFormData({ ...formData, gstNumber: e.target.value })}
                                    />
                                </div>
                                <div className="vw-form-group">
                                    <label className="vw-label">Payment Status <span>*</span></label>
                                    <select
                                        className="vw-select" required
                                        value={formData.paymentStatus} onChange={e => setFormData({ ...formData, paymentStatus: e.target.value })}
                                    >
                                        <option value="Pending">Pending</option>
                                        <option value="Paid">Paid</option>
                                        <option value="Overdue">Overdue</option>
                                    </select>
                                </div>
                            </div>
                            <button type="submit" className="vw-save-btn">Save Vendor</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
